/**
 * Spec 136 — UI affordance over the fan-out synthesize route.
 *
 *   POST /api/v1/brands/:id/fanout/synthesize
 *
 * Renders a "Run fan-out synthesis" button with a small cost-aware confirm
 * step (plain-language copy — clusters this brand's collected queries with a
 * low-cost AI pass, about a few cents). On confirm it POSTs the synthesize
 * route, disables the button + shows "Synthesizing…", then on success calls
 * `onComplete()` so the parent can refetch the graph + clusters, and toasts
 * "Synthesized N clusters" (N from the response, or the parent's refetched
 * count). On error it toasts the message and re-enables.
 *
 * The button doubles as a "Recompute" affordance when clusters already exist —
 * pass `recompute` to switch the label.
 *
 * data-testids:
 *   - run-fanout-synthesis          (the trigger)
 *   - run-fanout-synthesis-confirm  (the confirm popover's Proceed)
 *   - run-fanout-synthesis-cancel   (the confirm popover's Cancel)
 */
import { useEffect, useRef, useState } from 'react';
import { Icon, Spinner } from './ui/Icon.js';
import { Button } from './ui/Button.js';
import { useToast } from '../hooks/useToast.js';
import {
  synthesizeFanout,
  readSynthesisClusterCount,
} from '../api/fanout.js';

export interface RunFanoutSynthesisButtonProps {
  brandId: string;
  /**
   * Called after a successful synthesis so the parent can refetch its graph +
   * clusters. May return the refetched cluster count, which is used for the
   * success toast when the synthesize response did not carry a count.
   */
  onComplete: () => Promise<number | void> | number | void;
  /** Render as a "Recompute" affordance (clusters already exist). */
  recompute?: boolean;
}

export function RunFanoutSynthesisButton({
  brandId,
  onComplete,
  recompute = false,
}: RunFanoutSynthesisButtonProps) {
  const { addToast } = useToast();
  const [showConfirm, setShowConfirm] = useState(false);
  const [running, setRunning] = useState(false);
  const popoverRef = useRef<HTMLDivElement>(null);

  // Close the confirm popover on outside click (mirrors CollectButton).
  useEffect(() => {
    if (!showConfirm) return;
    function handleClick(e: MouseEvent) {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
        setShowConfirm(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [showConfirm]);

  async function runSynthesis() {
    if (running) return;
    setShowConfirm(false);
    setRunning(true);
    try {
      const summary = await synthesizeFanout(brandId);
      let count = readSynthesisClusterCount(summary);
      // Parent refetches the graph + clusters; fall back to its count when the
      // synthesize response did not name one.
      const refetched = await onComplete();
      if (count === null && typeof refetched === 'number') {
        count = refetched;
      }
      const noun = count === 1 ? 'cluster' : 'clusters';
      addToast(
        count === null
          ? 'Fan-out synthesis complete'
          : `Synthesized ${count} ${noun}`,
        'success',
      );
    } catch (err) {
      addToast(
        err instanceof Error ? err.message : 'Fan-out synthesis failed',
        'error',
      );
    } finally {
      setRunning(false);
    }
  }

  const label = recompute ? 'Recompute clusters' : 'Run fan-out synthesis';

  return (
    <div className="relative inline-block" ref={popoverRef}>
      <Button variant="primary" data-testid="run-fanout-synthesis" onClick={() => setShowConfirm((open) => !open)} disabled={running}>
        {running ? <Spinner /> : <Icon name="bx-sitemap" />}
        {running ? 'Synthesizing…' : label}
      </Button>

      {showConfirm && !running && (
        <div className="absolute left-0 top-full z-50 mt-2 w-80 rounded-lg border border-slate-200 bg-white p-4">
          <p className="text-sm text-slate-700">
            Run fan-out synthesis? This clusters this brand&apos;s collected
            queries using a low-cost AI pass (about a few cents).
          </p>
          <div className="mt-3 flex justify-end gap-2">
            <Button variant="ghost" data-testid="run-fanout-synthesis-cancel" onClick={() => setShowConfirm(false)}>
              Cancel
            </Button>
            <Button variant="primary" data-testid="run-fanout-synthesis-confirm" onClick={() => void runSynthesis()}>
              Proceed
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
